import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Image as ImageIcon, Trash2, Upload } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { cn } from "./cn.js";

const MAX_EDGE = 2000;

function processImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read-failed"));
    reader.onload = () => {
      const src = String(reader.result);
      const img = new Image();
      img.onerror = () => reject(new Error("decode-failed"));
      img.onload = () => {
        const k = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
        if (k === 1 && src.length < 900000) return resolve(src);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * k);
        canvas.height = Math.round(img.height * k);
        const ctx = canvas.getContext("2d");
        if (!ctx) return resolve(src);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  });
}

function Portal({ children }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;
  return createPortal(children, document.body);
}

function useAnchoredRect(anchorRef, open) {
  const [rect, setRect] = useState(null);
  useLayoutEffect(() => {
    if (!open || !anchorRef.current) {
      setRect(null);
      return;
    }
    const measure = () => {
      const r = anchorRef.current?.getBoundingClientRect?.();
      if (!r) return;
      setRect({
        left: r.left + window.scrollX,
        top: r.top + window.scrollY,
        right: r.right + window.scrollX,
        bottom: r.bottom + window.scrollY,
        width: r.width,
        height: r.height,
        vw: window.innerWidth,
        vh: window.innerHeight,
      });
    };
    measure();
    const timeout = window.setTimeout(measure, 16);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open, anchorRef]);
  return rect;
}

export function BackgroundControl({ background, onChange, onNotify, isLight, C }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const buttonRef = useRef(null);
  const popupRef = useRef(null);
  const rect = useAnchoredRect(buttonRef, open);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const btn = buttonRef.current;
      const pop = popupRef.current;
      if (btn && btn.contains(e.target)) return;
      if (pop && pop.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      onNotify("That file isn't an image");
      return;
    }
    setBusy(true);
    try {
      const data = await processImage(file);
      onChange({ image: data });
      onNotify("Background applied");
      setOpen(true);
    } catch {
      onNotify("Could not read that image");
    } finally {
      setBusy(false);
    }
  };

  const isActive = !!background.image || open;

  const popupPosition = useMemo(() => {
    if (!rect) return { top: -99999, left: 0 };
    const panelW = 236;
    let left = rect.left + rect.width / 2 - panelW / 2;
    const minLeft = Math.max(8, window.scrollX + 8);
    const maxLeft = window.scrollX + rect.vw - panelW - 8;
    left = Math.min(Math.max(left, minLeft), maxLeft);
    const top = rect.top - 272 - 6;
    return { top, left, panelW };
  }, [rect]);

  return (
    <>
      <div className="relative">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            handleFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />

        <div ref={buttonRef}>
          <ToggleButton
            isActive={isActive}
            busy={busy}
            open={open}
            background={background}
            onClick={() => (background.image ? setOpen((v) => !v) : fileRef.current?.click())}
            isLight={isLight}
            C={C}
          />
        </div>
      </div>
      {rect && (
        <Portal>
          <AnimatePresence>
            {open && (
              <motion.div
                ref={popupRef}
                initial={{ opacity: 0, y: 8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.96 }}
                transition={{ type: "spring", stiffness: 320, damping: 26 }}
                className="fixed z-[99999] w-[236px] rounded-2xl p-3.5 backdrop-blur-2xl"
                style={{
                  top: popupPosition.top,
                  left: popupPosition.left,
                  background: isLight ? "rgba(255,255,255,0.95)" : "rgba(14,15,19,0.94)",
                  border: `1px solid ${isLight ? "rgba(15,23,42,0.1)" : "rgba(255,255,255,0.1)"}`,
                  boxShadow: isLight
                    ? "0 24px 60px -16px rgba(15,23,42,0.2)"
                    : "0 24px 60px -16px rgba(0,0,0,0.9)",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <p
                  className="mb-2.5 font-mono text-[8.5px] uppercase tracking-[0.25em]"
                  style={{ color: isLight ? "rgba(15,23,42,0.35)" : "rgba(255,255,255,0.35)" }}
                >
                  Backdrop
                </p>

                {background.image && (
                  <div
                    className="mb-3 h-16 w-full rounded-lg bg-cover bg-center"
                    style={{
                      border: `1px solid ${isLight ? "rgba(15,23,42,0.1)" : "rgba(255,255,255,0.1)"}`,
                      backgroundImage: `url(${background.image})`,
                    }}
                  />
                )}

                <Slider
                  label="Dim"
                  value={background.dim}
                  min={0}
                  max={0.95}
                  step={0.05}
                  onChange={(v) => onChange({ dim: v })}
                  isLight={isLight}
                  C={C}
                />
                <Slider
                  label="Blur"
                  value={background.blur}
                  min={0}
                  max={16}
                  step={1}
                  unit="px"
                  onChange={(v) => onChange({ blur: v })}
                  isLight={isLight}
                  C={C}
                />

                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-[11.5px] font-semibold transition-all active:scale-[0.97]"
                    style={{
                      background: C.accent,
                      color: C.accentOnBg,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.filter = "brightness(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.filter = "brightness(1)";
                    }}
                  >
                    <Upload size={12} strokeWidth={2.4} />
                    Replace
                  </button>
                  <button
                    onClick={() => {
                      onChange({ image: null });
                      setOpen(false);
                      onNotify("Background removed");
                    }}
                    aria-label="Remove background"
                    title="Remove background"
                    className="flex h-[34px] w-[34px] items-center justify-center rounded-lg border transition-colors hover:bg-[rgba(255,107,107,0.16)]"
                    style={{
                      borderColor: "rgba(255,107,107,0.25)",
                      background: "rgba(255,107,107,0.08)",
                      color: "#ff8f8f",
                    }}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Portal>
      )}
    </>
  );
}

function ToggleButton({ isActive, busy, open, background, onClick, isLight, C }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      aria-label="Background image"
      title="Background image"
      aria-haspopup="dialog"
      aria-expanded={open}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors duration-200"
      style={{
        color: isActive
          ? C.accent
          : hover
            ? (isLight ? "#0f172a" : "#ffffff")
            : (isLight ? "rgba(15,23,42,0.55)" : "rgba(255,255,255,0.55)"),
        background: isActive
          ? C.accentBg
          : hover
            ? (isLight ? "rgba(15,23,42,0.06)" : "rgba(255,255,255,0.07)")
            : "transparent",
      }}
    >
      <ImageIcon size={15} className={busy ? "animate-pulse" : undefined} />
    </button>
  );
}

function Slider({ label, value, min, max, step, unit, onChange, isLight, C }) {
  const labelMute = isLight ? "rgba(15,23,42,0.35)" : "rgba(255,255,255,0.35)";
  const valueMute = isLight ? "rgba(15,23,42,0.6)" : "rgba(255,255,255,0.6)";
  return (
    <label className="mb-2 block">
      <span className="mb-1 flex items-center justify-between">
        <span
          className="font-mono text-[9px] uppercase tracking-[0.2em]"
          style={{ color: labelMute }}
        >
          {label}
        </span>
        <span
          className="font-mono text-[10px] tabular-nums"
          style={{ color: valueMute }}
        >
          {unit ? `${value}${unit}` : `${Math.round(value * 100)}%`}
        </span>
      </span>
      <input
        type="range"
        className="range-lime w-full"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}
